import type { ReactNode } from 'react'
import { ChevronRight, UserPlus } from 'lucide-react'
import type { ClientOngoingLoad } from '@numeo/types-core'
import type { TmsInvoiceRow } from '@numeo/types-main-web-app'
import { Avatar, Badge } from '@/components/ds2'
import { useOpenAssignmentModal } from '@/pages/dashboard/pages/updater-agent/pages/ongoing-loads/pages/load-detail/hooks/useOpenAssignmentModal'
import { SectionLabel } from './ReviewNotice'

interface DriverLineProps {
    row: TmsInvoiceRow
    /** The load as the board has it; the row waits for it (the modal assigns that load). */
    load: ClientOngoingLoad | undefined
    /** The driver was clicked: the board's modal is open, and the review steps aside for it. */
    onAssign: () => void
}

/** Initials for the avatar: the first letters of the first two words of the name. */
const initialsOf = (name: string): string =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0]?.toUpperCase())
        .join('')

/** What the driver row shows: the named driver (with a No truck badge when the truck is missing), or the call to assign one. */
const DriverContent = ({ row }: { row: TmsInvoiceRow }) => {
    const noTruck = row.driver_assigned === false
    if (!row.driver)
        return (
            <>
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-soft text-accent-soft-foreground" aria-hidden>
                    <UserPlus className="h-4 w-4" />
                </span>
                <span className="flex-1 text-sm font-medium text-accent">Assign driver</span>
            </>
        )
    return (
        <>
            <Avatar variant="letterSoft" type="default" value={initialsOf(row.driver)} size={32} />
            <span className="flex min-w-0 flex-1 items-center gap-2">
                <span className="truncate text-sm font-medium text-foreground">{row.driver}</span>
                {noTruck && <Badge variant="soft" type="warning" size="md" label="No truck" />}
            </span>
            <span className="text-xs text-foreground-muted">{noTruck ? 'Assign truck' : 'Change'}</span>
        </>
    )
}

const RowFrame = ({
    dashed,
    disabled,
    label,
    onClick,
    children,
}: {
    dashed: boolean
    disabled?: boolean
    label: string
    onClick?: () => void
    children: ReactNode
}) => (
    <button
        type="button"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors duration-150 hover:bg-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent ${dashed ? 'border-dashed border-border' : 'border-border'}`}
    >
        {children}
    </button>
)

/** The row as a button that opens the board's modal; disabled with the board's reason when the load is locked (split, payroll locked). */
const DriverButton = ({ load, row, onAssign }: { load: ClientOngoingLoad; row: TmsInvoiceRow; onAssign: () => void }) => {
    const { open, isDisabled, disabledReason } = useOpenAssignmentModal(load, null)
    return (
        <>
            <RowFrame
                dashed={!row.driver}
                disabled={isDisabled}
                label={row.driver ? `Change driver: ${row.driver}` : 'Assign driver'}
                onClick={() => {
                    onAssign()
                    open()
                }}
            >
                <DriverContent row={row} />
                {!isDisabled && <ChevronRight className="h-4 w-4 text-foreground-muted" aria-hidden />}
            </RowFrame>
            {disabledReason && <p className="mt-1.5 text-xs text-foreground-muted">{disabledReason}</p>}
        </>
    )
}

/**
 * The review's driver: who drove the load, as a row that opens the board's own Assign / Change (the load page's modal, under the
 * board's rule). The name is the button; a load with no driver shows the call to assign one, and a load with no truck says that
 * invoicing does not wait for it.
 */
export const DriverLine = ({ row, load, onAssign }: DriverLineProps) => (
    <section aria-label="Driver">
        <SectionLabel>Driver</SectionLabel>
        {load ? (
            <DriverButton load={load} row={row} onAssign={onAssign} />
        ) : (
            <RowFrame dashed={!row.driver} disabled label={row.driver ? `Change driver: ${row.driver}` : 'Assign driver'}>
                <DriverContent row={row} />
            </RowFrame>
        )}
        {row.driver_assigned === false && (
            <p role="note" className="mt-2 rounded-xl bg-warning-soft px-3 py-2 text-xs leading-relaxed text-warning-soft-foreground">
                {row.driver ? 'No truck on this load.' : 'No driver on this load.'} You can still invoice it; assign the truck that ran it so its driver and pay
                are on record.
            </p>
        )}
    </section>
)
